import { Notify } from 'quasar'
import { setTimeout } from 'timers'
import { Vue } from 'vue-property-decorator'

export default {
  /**
   * Install the System Messages class.
   * @param {Object} Vue The global Vue.
   * @param {Object} options Any options we want to have in our plugin.
   * @return {void}
   */
  install (Vue, options = {}) {
    this.notify = Notify
    this.__setDefaultConfig(options)
    Vue.prototype.$messageBox = this
  },

  __setDefaultConfig (options) {
    this.MESSAGE_DEFAULT_CONFIG = {
      color: options.color || 'positive',
      icon: '',
      message: '',
      detail: '',
      position: options.position || 'top-right',
      actions: []
    }
  },

  __getDefaultConfig () {
    return Object.freeze(this.MESSAGE_DEFAULT_CONFIG)
  },

  /**
   * Shows a feedback message to the user
   *
   * @param {Object} message - The message object
   * @param {String} message.icon - Icon which must be shown in the message box
   * @param {String} message.content - A plain text or a HTML to display as the message text.
   * @param {String} message.enterAnimation - The transition animation for displaying the alert.
   * @param {String} message.leaveAnimation - The transition animation for dismissing the alert.
   * @param {String} message.color - This can be any color set up under the Quasar Color Palette. <http://quasar-framework.org/components/color-palette.html>
   * @param {String} message.position - Controls the position of the alerts, when it is displayed. The available positions are top, top-center, top-right, right, bottom-right, bottom, bottom-center, bottom-left, left and top-left
   * @param {String} message.actions - Place one or more buttons within the alert, like “Snooze” or “Abort”. Specify label (String) and handler (Function).
   * @param {boolean} [autoclose=false] - whether it mast close automatically
   */
  showMessage (message, autoclose = false) {
    const currentAlert = this.notify.create(
      Object.assign({}, this.__getDefaultConfig(), {
        icon: message.icon,
        message: message.content,
        detail: message.detail,
        color: message.color || this.MESSAGE_DEFAULT_CONFIG.color,
        position: message.position || this.MESSAGE_DEFAULT_CONFIG.position,
        actions: message.actions || this.MESSAGE_DEFAULT_CONFIG.actions
      })
    )

    if (autoclose) {
      setTimeout(() => {
        currentAlert.dismiss()
      }, 3000)
    }
  },

  /**
   * Shows a Success Message to the user
   *
   * @param {String} content - A plain text or a HTML to display as the message text.
   */
  success (content, detail = '') {
    const SUCCESS_MESSAGE = {
      icon: 'done',
      color: 'positive',
      content: content,
      detail: detail
    }

    this.showMessage(SUCCESS_MESSAGE, true)
  },

  /**
   * Shows a Warning Message to the user
   *
   * @param {String} content - A plain text or a HTML to display as the message text.
   */
  warning (content, detail = '') {
    const WARNING_MESSAGE = {
      icon: 'warning',
      color: 'warning',
      content: content,
      detail: detail
    }

    this.showMessage(WARNING_MESSAGE)
  },

  /**
   * Shows a Info Message to the user
   *
   * @param {String} content - A plain text or a HTML to display as the message text.
   */
  info (content, detail = '') {
    const INFO_MESSAGE = {
      icon: 'info',
      color: 'info',
      content: content,
      detail: detail
    }

    this.showMessage(INFO_MESSAGE)
  },

  /**
   * Shows a Error Message to the user
   *
   * @param {String} content - A plain text or a HTML to display as the message text.
   */
  error (content, detail = '') {
    const ERROR_MESSAGE = {
      icon: 'error',
      color: 'negative',
      position: 'top',
      content: content,
      detail: detail
    }
    this.showMessage(ERROR_MESSAGE)
  }
}

export const JtechMessage = {

  _message: function () {
    return Vue.prototype.$messageBox
  },

  success: function (content, position = 'top-right') {
    this._message().showSuccess(content, position, null, false)
  },

  warn: function (content, position = '') {
    this._message().showWarning(content, position, null, false)
  },

  info: function (content, position = '') {
    this._message().showInfo(content, position, null, false)
  },

  error: function (content, position = '') {
    this._message().showError(content, position, null, false)
  },

  getText: function (messageKey) {
    return Vue.prototype.$gettext(messageKey)
  }
}
